import type { CameraPose } from "@workspace/blocks";
import { useCallback, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { useTranslation } from "../../i18n";
import { parseDescriptionWithMetadata } from "../../utils/splatMetadata";
import { GaussianViewer } from "../GaussianViewer";
import { Alert, Button, Modal } from "../ui";
import type { UploadRow } from "./types";

export function ViewpointDialog({
	upload,
	onClose,
	onSaved,
}: { upload: UploadRow; onClose: () => void; onSaved: (updated: UploadRow) => void }) {
	const ja = useTranslation().locale === "ja";
	const utils = trpc.useUtils();
	const editing = trpc.library.getForEditing.useQuery({ id: upload.id }, { retry: false, refetchOnWindowFocus: false });
	const save = trpc.library.saveCameraPose.useMutation({ networkMode: "always" });
	const camera = useRef<CameraPose | null>(null);
	const [hasPose, setHasPose] = useState(false);
	const [ready, setReady] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const busy = save.isLoading;
	const metadata = parseDescriptionWithMetadata(editing.data?.description).metadata;
	const onCameraChange = useCallback((pose: CameraPose) => {
		camera.current = pose;
		setHasPose(true);
	}, []);
	const commit = async (pose: CameraPose | null) => {
		if (!editing.data || busy || editing.isFetching) return;
		setError(null);
		try {
			const updated = await save.mutateAsync({ id: upload.id, revision: editing.data.revision, camera: pose });
			await Promise.all([
				utils.library.listMine.invalidate(),
				utils.library.getForEditing.invalidate({ id: upload.id }),
				utils.viewer.getGaussianSplat.invalidate({ shareId: upload.shareId }),
			]);
			onSaved(updated);
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : String(cause));
		}
	};
	return (
		<Modal
			open
			className="account-surface account-dialog account-viewpoint-dialog sm:max-w-4xl"
			busy={busy}
			onOpenChange={open => {
				if (!open) onClose();
			}}
			title={ja ? "最初の視点を設定" : "Set the starting viewpoint"}
			closeLabel={ja ? "閉じる" : "Close"}
			description={
				ja
					? "画面を操作して、最初に表示する位置と向きを決めてください。歩行モードでも保存できます。"
					: "Move through the scene and choose the position and direction visitors see first. Walking works too."
			}
			footer={
				<>
					<Button
						variant="ghost"
						disabled={busy || editing.isFetching || !metadata?.camera}
						onClick={() => void commit(null)}
					>
						{ja ? "開始視点を自動に戻す" : "Restore automatic viewpoint"}
					</Button>
					<Button
						disabled={busy || editing.isFetching || !ready || !hasPose}
						onClick={() => {
							if (camera.current) void commit(camera.current);
						}}
					>
						{ja ? "この視点を開始視点として保存" : "Save this as the starting view"}
					</Button>
				</>
			}
		>
			<div className="account-dialog-body min-w-0">
				<div className="account-preview-heading">
					<p>{upload.title || upload.shareId}</p>
					{editing.data && (
						<span className="account-status-label">
							{metadata?.camera
								? ja
									? "開始視点を設定済み"
									: "Starting view saved"
								: ja
									? "開始視点は自動"
									: "Automatic starting view"}
						</span>
					)}
				</div>
				<div className="account-viewpoint-preview h-[min(60dvh,440px)] min-h-60 w-full overflow-hidden">
					{!editing.data && !editing.error && (
						<output className="account-preview-loading">{ja ? "モデルを読み込み中…" : "Loading the model…"}</output>
					)}
					{editing.data && (
						<GaussianViewer
							format={editing.data.modelType === "spz" && !editing.data.streamed ? "spz" : "ply"}
							src={editing.data.viewerUrl ?? editing.data.editorUrl}
							initialCameraPose={metadata?.camera}
							centerMode={metadata?.centerMode}
							collision={metadata?.collision}
							onCameraChange={onCameraChange}
							onAssetProgress={progress => {
								if (progress >= 1) {
									setReady(true);
									setError(null);
								}
							}}
							onError={cause => {
								setReady(false);
								setError(cause.message);
							}}
						/>
					)}
				</div>
				<div className="account-preview-heading">
					<p className="account-dialog-caption">{ja ? "ドラッグで向きと位置を調整" : "Drag to adjust the view"}</p>
					<p className="account-dialog-caption">
						{ja ? "歩行モードからも保存できます" : "You can also save from walking mode"}
					</p>
				</div>
				{busy && (
					<div className="account-progress" data-active>
						<output aria-live="polite">
							<span className="account-progress-dot" aria-hidden="true" />
							{ja ? "開始視点を保存中…" : "Saving the starting viewpoint…"}
						</output>
					</div>
				)}
				<p className="account-dialog-caption">
					{ja
						? "開始視点だけを保存します。モデル・LOD・当たり判定・共有URLは維持されます。"
						: "Save the starting view while preserving the model, LOD, collision and share URL."}
				</p>
				{(error || editing.error) && <Alert tone="error">{error || editing.error?.message}</Alert>}
			</div>
		</Modal>
	);
}
